import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useNavigationService } from '../../../../../use-cases/navigation'
import { useSettingsService } from '../../../../../use-cases/settings'
import { useAppSelector } from '../../../../../application/states/hooks'
import { faqIntro, faqSections } from './faqData'

const TERMOS_DE_USO_URL = 'https://veles.com.br/termos-de-uso/'

export const FaqsSection = () => {
  const theme = useTheme()
  const { navigateTo } = useNavigationService()
  const { setActiveTab } = useSettingsService()
  const company = useAppSelector((state) => state.company.company)
  const resaleConfig = useAppSelector((state) => state.resaleConfig.resaleConfig)

  const brandName =
    company?.resale === true || company?.resaleId
      ? resaleConfig?.name || company?.nomeFantasia || company?.razaoSocial || 'Lurik'
      : 'Lurik'

  const introText = brandName === 'Lurik'
    ? faqIntro
    : faqIntro.replace(/Lurik 360°|plataforma Lurik/gi, `plataforma ${brandName}`)

  /** Para revenda: troca Lurik pelo nome da revenda e remove referências a Veles */
  const textForRevenda = (text: string) => {
    if (brandName === 'Lurik') return text
    return text
      .replace(/Lurik/gi, brandName)
      .replace(/Database Veles/gi, 'base de dados da plataforma')
      .replace(/\s+da Veles Marketing Solutions/gi, '')
      .replace(/Veles Marketing Solutions/gi, 'suporte')
      .replace(/\bVeles\b/gi, 'plataforma')
  }

  const goToAjuda = () => {
    setActiveTab({ tab: 'help' })
    navigateTo({ route: '/workspace/settings' })
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography
        variant="h5"
        fontWeight="700"
        color="primary"
        gutterBottom
        sx={{ mb: 1 }}
      >
        FAQ {brandName}: Perguntas Frequentes e Soluções
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        {introText}
      </Typography>

      {faqSections.map((section, sectionIndex) => (
        <Paper
          key={sectionIndex}
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <Accordion
            defaultExpanded={false}
            disableGutters
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              borderRadius: 0,
              '&.Mui-expanded': { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="primary" />}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                borderBottom: `1px solid ${theme.palette.divider}`,
                minHeight: 56,
                '& .MuiAccordionSummary-content': { my: 1.5 },
                '&.Mui-expanded': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              <Typography variant="subtitle1" fontWeight="600" color="primary">
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1.5, pb: 2, backgroundColor: 'background.paper' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.6 }}
              >
                {section.description}
              </Typography>
              {section.items
                .filter((item) => brandName === 'Lurik' || !item.question.toLowerCase().includes('white label'))
                .map((item, itemIndex) => {
                const questionText = textForRevenda(item.question)
                const answerText = textForRevenda(item.answer)
                return (
                  <Accordion
                    key={itemIndex}
                    disableGutters
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      mb: 1,
                      '&:last-of-type': { mb: 0 },
                      '&.Mui-expanded': { margin: '0 0 8px 0' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': { my: 0.5 },
                      }}
                    >
                      <Typography variant="body2" fontWeight="500" color="text.primary">
                        {questionText}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {answerText}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </AccordionDetails>
          </Accordion>
        </Paper>
      ))}

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ lineHeight: 1.8 }}
        >
          <Box component="span" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
            Referências
          </Box>
          {brandName === 'Lurik' && (
            <Box component="span" sx={{ display: 'block', mb: 0.75 }}>
              [1]{' '}
              <Link
                component="button"
                variant="caption"
                onClick={goToAjuda}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Guia de Usabilidade Lurik v1.0 (Janeiro/2026).
              </Link>
            </Box>
          )}
          <Box component="span" sx={{ display: 'block', mb: 0.75 }}>
            {brandName === 'Lurik' ? '[2]' : '[1]'} Lei Geral de Proteção de Dados (Lei nº 13.709/2018).
          </Box>
          {brandName === 'Lurik' && (
            <Box component="span" sx={{ display: 'block' }}>
              [3]{' '}
              <Link
                href={TERMOS_DE_USO_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Termos de Uso e Políticas de Privacidade Veles Marketing Solutions.
              </Link>
            </Box>
          )}
        </Typography>
      </Paper>
    </Box>
  )
}
